import { create } from 'zustand';

import { EMPTY_STR } from '@/utils/constants/common';

export type PageTypeStoreState = {
    pageType: string;
};

export type PageTypeStoreActions = {
    setPageType: (pageType: string) => void;
    reset: () => void;
};

export type PageTypeStoreType = PageTypeStoreState & PageTypeStoreActions;

const INITIAL_STATE: PageTypeStoreState = {
    pageType: EMPTY_STR,
};

const createPageTypeStore = () =>
    create<PageTypeStoreType>((set) => ({
        ...INITIAL_STATE,
        setPageType: (pageType) => {
            if (pageType) {
                set({ pageType });
            }
        },
        reset: () => set(INITIAL_STATE),
    }));

const PAGE_TYPE_STORE_GLOBAL_KEY = '__tnPageTypeStore__';

type PageTypeStoreGlobal = typeof globalThis & {
    [PAGE_TYPE_STORE_GLOBAL_KEY]?: ReturnType<typeof createPageTypeStore>;
};

/**
 * The page's tracking `pageType`, declared by the page and read by tracking across the app.
 *
 * One client instance shared by every app bundle: the declaring page and the appshell's snapshot
 * hook live in different remotes, so a per-bundle store would split them.
 *
 * SSR-WRITE SAFETY: the server instance is shared by every request — only mutate from client
 * effects and handlers, never during render.
 */
export const usePageTypeStore =
    typeof window === 'undefined'
        ? createPageTypeStore()
        : ((window as PageTypeStoreGlobal)[PAGE_TYPE_STORE_GLOBAL_KEY] ??= createPageTypeStore());

/**
 * The page type as of now, rather than as of the last render. Prefer this in effects, router
 * events and handlers: pages declare in a layout effect, so a render closure can name the screen
 * just left. Components that render the value should subscribe with
 * `usePageTypeStore((state) => state.pageType)` instead.
 */
export const getPageType = (): string => usePageTypeStore.getState().pageType;

/** Declares the page type from outside React (tracking pipeline). {@link usePageTypes} for pages. */
export const setPageType = (pageType: string): void => {
    usePageTypeStore.getState().setPageType(pageType);
};
